import type { DocumentNode } from 'graphql'

import type { IMutation } from '~/client/graph'
import {
  DisableLocationsAndClearControlsDocument,
  PreviewResourceControlCascadeDocument,
  SaveManyWorkflowLocationRestrictionsDocument,
  UpdateLocationsIsBookableDocument,
  UpdateLocationsOperatingIntervalsDocument,
  UpdateLocationsPermittedCompaniesDocument,
  UpdateLocationsShowInMapDocument,
  UpdateResourceControlsDocument,
} from '~/client/graph/operations/generated/LocationType.generated'
import type {
  ProjectResourceOperation,
  ProjectResourceOperationFailure,
} from '~/client/src/shared/resources/domain/projectResourceType'
import type {
  IProposedResourceControls,
  IResourceControlCascade,
  IResourceControlCommands,
  IResourceControlsUpdate,
  IWorkflowRestrictionDraft,
} from '~/client/src/shared/resources/domain/resourceControls'
import type { ResourceResult } from '~/client/src/shared/resources/domain/resourceResult'
import {
  resourceErr,
  resourceOk,
} from '~/client/src/shared/resources/domain/resourceResult'
import type IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import { withSingleDailyInterval } from '~/client/src/shared/utils/operatingIntervals'

import type GraphExecutorStore from './GraphExecutor.store'

type LocationControlMutationField =
  | 'updateLocationsPermittedCompanies'
  | 'updateLocationsIsBookable'
  | 'updateLocationsShowInMap'
  | 'updateLocationsOperatingIntervals'

interface RestrictionReconciliation {
  readonly prepareReplacement: (
    projectId: string,
    recordIds: readonly string[],
  ) => (successfulRecordIds: readonly string[]) => void
}

/** Store-owned command module for resource controls. */
export default class ProjectResourceControls implements IResourceControlCommands {
  public constructor(
    private readonly graph: Pick<GraphExecutorStore, 'query' | 'mutate'>,
    private readonly restrictions: RestrictionReconciliation,
  ) {}

  /** Updates permitted companies; the location subscription applies the result. */
  public savePermittedCompanies(
    projectId: string,
    recordIds: readonly string[],
    companyIds: readonly string[],
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutateLocations(
      'updateLocationsPermittedCompanies',
      UpdateLocationsPermittedCompaniesDocument,
      projectId,
      recordIds,
      { permittedCompanies: [...companyIds] },
    )
  }

  /** Updates workflow-booking availability. */
  public saveBookable(
    projectId: string,
    recordIds: readonly string[],
    isBookable: boolean,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutateLocations(
      'updateLocationsIsBookable',
      UpdateLocationsIsBookableDocument,
      projectId,
      recordIds,
      { isBookable },
    )
  }

  /** Updates map visibility. */
  public saveShowInMap(
    projectId: string,
    recordIds: readonly string[],
    showInMap: boolean,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutateLocations(
      'updateLocationsShowInMap',
      UpdateLocationsShowInMapDocument,
      projectId,
      recordIds,
      { showInMap },
    )
  }

  /** Updates operating intervals. */
  public saveOperatingIntervals(
    projectId: string,
    recordIds: readonly string[],
    intervals: readonly IMsDateInterval[],
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutateLocations(
      'updateLocationsOperatingIntervals',
      UpdateLocationsOperatingIntervalsDocument,
      projectId,
      recordIds,
      {
        // Collapsed to a single daily window last, so no write can persist a
        // second one — this layer is the only way intervals reach the server.
        operatingIntervals: withSingleDailyInterval(intervals).map(
          ({ startDate, endDate, isDaily, dayOfWeek }) => ({
            startDate,
            endDate,
            isDaily,
            dayOfWeek,
          }),
        ),
      },
    )
  }

  /** Replaces workflow restrictions; their subscription applies the result. */
  public async saveWorkflowRestrictions(
    projectId: string,
    recordIds: readonly string[],
    restrictions: readonly IWorkflowRestrictionDraft[],
  ): Promise<
    ResourceResult<readonly string[], ProjectResourceOperationFailure>
  > {
    const result = await this.executeMutation(
      'UpdateControls',
      SaveManyWorkflowLocationRestrictionsDocument,
      {
        workflowLocationRestrictions: recordIds.flatMap(locationId =>
          restrictions.map(restriction => ({
            id: restriction.id,
            locationId,
            projectId,
            restrictionType: restriction.restrictionType,
            values: [...restriction.values],
            workflowIds: [...restriction.workflowIds],
          })),
        ),
      },
    )
    if (result._tag === 'Err') return result
    return resourceOk(
      (result.value.saveManyWorkflowLocationRestrictions ?? []).flatMap(
        restriction =>
          typeof restriction?.id === 'string' ? [restriction.id] : [],
      ),
    )
  }

  /** Atomically disables resources and clears their dependent controls. */
  public async disableAndClearControls(
    projectId: string,
    recordIds: readonly string[],
  ): Promise<
    ResourceResult<
      IResourceControlCascade | null,
      ProjectResourceOperationFailure
    >
  > {
    const reconcileRestrictions = this.restrictions.prepareReplacement(
      projectId,
      recordIds,
    )
    const result = await this.executeMutation(
      'UpdateControls',
      DisableLocationsAndClearControlsDocument,
      { projectId, locationIds: [...recordIds] },
    )
    if (result._tag === 'Err') return result
    const cascade = result.value.disableLocationsAndClearControls
    if (!cascade) {
      return this.failure(
        'UpdateControls',
        new Error('Resources were not disabled'),
      )
    }
    reconcileRestrictions(this.successfulRecordIds(recordIds, cascade))
    return resourceOk(cascade)
  }

  /** Computes the cascade impact without mutating controls. */
  public async previewControlCascade(
    projectId: string,
    recordIds: readonly string[],
    proposed: IProposedResourceControls,
  ): Promise<
    ResourceResult<
      IResourceControlCascade | null,
      ProjectResourceOperationFailure
    >
  > {
    try {
      const result = await this.graph.query(
        PreviewResourceControlCascadeDocument,
        {
          projectId,
          locationIds: [...recordIds],
          proposedIsBookable: proposed.isBookable,
        },
      )
      if (result.error) {
        return this.failure('PreviewControlCascade', result.error)
      }
      return resourceOk(result.data?.previewResourceControlCascade ?? null)
    } catch (cause: unknown) {
      return this.failure('PreviewControlCascade', cause)
    }
  }

  /** Saves all provided control slices atomically. */
  public async saveControls(
    projectId: string,
    recordIds: readonly string[],
    update: IResourceControlsUpdate,
  ): Promise<
    ResourceResult<
      IResourceControlCascade | null,
      ProjectResourceOperationFailure
    >
  > {
    if (
      update.workflowRestrictions !== undefined &&
      update.isBookable === undefined
    ) {
      return this.failure(
        'UpdateControls',
        new Error('Restriction replacements must publish a location update'),
      )
    }
    const reconcileRestrictions =
      update.workflowRestrictions === undefined
        ? null
        : this.restrictions.prepareReplacement(projectId, recordIds)
    const result = await this.executeMutation(
      'UpdateControls',
      UpdateResourceControlsDocument,
      {
        projectId,
        locationIds: [...recordIds],
        permittedCompanies: update.permittedCompanies
          ? [...update.permittedCompanies]
          : undefined,
        // Collapsed the same way the dedicated write is.
        operatingIntervals: update.operatingIntervals
          ? withSingleDailyInterval(update.operatingIntervals).map(
              ({ startDate, endDate, isDaily, dayOfWeek }) => ({
                startDate,
                endDate,
                isDaily,
                dayOfWeek,
              }),
            )
          : undefined,
        isBookable: update.isBookable,
        workflowRestrictions: update.workflowRestrictions?.map(restriction => ({
          restrictionType: restriction.restrictionType,
          workflowIds: [...restriction.workflowIds],
          values: [...restriction.values],
        })),
        closures: update.closures?.map(({ startDate, endDate, isDaily }) => ({
          startDate,
          endDate,
          isDaily,
        })),
      },
    )
    if (result._tag === 'Err') return result
    const cascade = result.value.updateResourceControls
    if (!cascade) {
      return this.failure(
        'UpdateControls',
        new Error('Resource controls were not updated'),
      )
    }
    if (update.workflowRestrictions !== undefined) {
      reconcileRestrictions?.(this.successfulRecordIds(recordIds, cascade))
    }
    return resourceOk(cascade)
  }

  private async mutateLocations(
    field: LocationControlMutationField,
    mutation: DocumentNode,
    projectId: string,
    recordIds: readonly string[],
    variables: Readonly<Record<string, unknown>>,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    const result = await this.executeMutation('UpdateControls', mutation, {
      projectId,
      locationIds: [...recordIds],
      ...variables,
    })
    if (result._tag === 'Err') return result

    const updatedIds = new Set(
      (result.value[field] ?? []).flatMap(id =>
        typeof id === 'string' ? [id] : [],
      ),
    )
    const missingIds = recordIds.filter(id => !updatedIds.has(id))
    return missingIds.length === 0
      ? resourceOk(undefined)
      : this.failure(
          'UpdateControls',
          new Error(
            `Resource controls were not updated: ${missingIds.join(', ')}`,
          ),
        )
  }

  private successfulRecordIds(
    recordIds: readonly string[],
    cascade: IResourceControlCascade,
  ): string[] {
    const failedIds = new Set(cascade.failedLocationIds ?? [])
    return recordIds.filter(id => !failedIds.has(id))
  }

  private async executeMutation(
    operation: ProjectResourceOperation,
    mutation: DocumentNode,
    variables: Readonly<Record<string, unknown>>,
  ): Promise<ResourceResult<IMutation, ProjectResourceOperationFailure>> {
    try {
      const result = await this.graph.mutate(mutation, variables)
      if (result.error) return this.failure(operation, result.error)
      return resourceOk(result.data ?? {})
    } catch (cause: unknown) {
      return this.failure(operation, cause)
    }
  }

  private failure(
    operation: ProjectResourceOperation,
    cause: unknown,
  ): ResourceResult<never, ProjectResourceOperationFailure> {
    return resourceErr({
      _tag: 'ProjectResourceOperationFailure',
      operation,
      cause,
    })
  }
}
